function finitePoint(point) {
  const x = Number(point?.xInches ?? point?.x);
  const y = Number(point?.yInches ?? point?.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function positiveFinite(value, label) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    throw new RangeError(`${label} must be a positive finite number.`);
  }
  return numeric;
}

/** Build an equal-scale physical viewport containing the target and every impact. */
export function engagementZonePlotGeometry(analysis, {
  aspectRatio = 4 / 3,
  marginRatio = 0.08,
  minimumMarginInches = 0.75,
} = {}) {
  const target = analysis?.target;
  if (!target || !Array.isArray(target.polygonInches)) {
    throw new TypeError("Engagement Zone target geometry is required.");
  }
  const desiredAspect = positiveFinite(aspectRatio, "Plot aspect ratio");
  const relativeMargin = Number(marginRatio);
  const minimumMargin = Number(minimumMarginInches);
  if (!Number.isFinite(relativeMargin) || relativeMargin < 0
    || !Number.isFinite(minimumMargin) || minimumMargin < 0) {
    throw new RangeError("Plot margins must be finite and non-negative.");
  }

  const targetPoints = target.polygonInches.map(finitePoint);
  if (targetPoints.some((point) => point === null) || targetPoints.length < 3) {
    throw new TypeError("Engagement Zone target polygon is invalid.");
  }
  const impactPoints = (analysis.impacts || []).map(finitePoint).filter(Boolean);
  const points = [...targetPoints, ...impactPoints];
  let minX = Math.min(...points.map(({ x }) => x));
  let maxX = Math.max(...points.map(({ x }) => x));
  let minY = Math.min(...points.map(({ y }) => y));
  let maxY = Math.max(...points.map(({ y }) => y));
  const unpaddedSpan = Math.max(maxX - minX, maxY - minY, 1);
  const margin = Math.max(minimumMargin, unpaddedSpan * relativeMargin);
  minX -= margin;
  maxX += margin;
  minY -= margin;
  maxY += margin;

  let width = maxX - minX;
  let height = maxY - minY;
  if (width / height < desiredAspect) {
    const expansion = (height * desiredAspect - width) / 2;
    minX -= expansion;
    maxX += expansion;
  } else if (width / height > desiredAspect) {
    const expansion = (width / desiredAspect - height) / 2;
    minY -= expansion;
    maxY += expansion;
  }
  width = maxX - minX;
  height = maxY - minY;
  const scale = Math.max(width, height);

  return Object.freeze({
    minX,
    maxX,
    minY,
    maxY,
    width,
    height,
    // SVG's vertical coordinate grows downward, so invert the physical y axis.
    viewBox: Object.freeze({ x: minX, y: -maxY, width, height }),
    markerRadius: Math.min(0.35, Math.max(0.07, scale / 300)),
    strokeWidth: Math.min(0.18, Math.max(0.04, scale / 500)),
  });
}

export function engagementZoneSvgPoint(point) {
  const value = finitePoint(point);
  if (!value) throw new TypeError("Impact coordinates must be finite.");
  return Object.freeze({ x: value.x, y: -value.y });
}

function scoringPolygon(points, label) {
  if (!Array.isArray(points) || points.length < 3) {
    throw new TypeError(`${label} polygon is invalid.`);
  }
  return Object.freeze(points.map((point) => engagementZoneSvgPoint(point)));
}

/** Convert optional USPSA scoring artwork without changing hit semantics. */
export function engagementZoneScoringOverlayGeometry(target) {
  const overlay = target?.scoringOverlayInches;
  if (overlay === undefined || overlay === null) return null;
  if (!Array.isArray(overlay.zoneBoundaries)
    || !Array.isArray(overlay.zoneLabels)) {
    throw new TypeError("Engagement Zone scoring overlay is invalid.");
  }
  const borderPolygon = scoringPolygon(
    overlay.borderPolygon, "Engagement Zone scoring border",
  );
  const zoneBoundaries = Object.freeze(overlay.zoneBoundaries.map((boundary) => {
    if (!/^[a-z][a-z0-9-]*$/.test(boundary?.id || "")) {
      throw new TypeError("Engagement Zone scoring boundary id is invalid.");
    }
    return Object.freeze({
      id: boundary.id,
      points: scoringPolygon(
        boundary.points, `Engagement Zone ${boundary.id} boundary`,
      ),
    });
  }));
  const zoneLabels = Object.freeze(overlay.zoneLabels.map((label) => {
    if (!/^[ABCD]$/.test(label?.zone || "")) {
      throw new TypeError("Engagement Zone scoring label is invalid.");
    }
    const point = engagementZoneSvgPoint(label);
    return Object.freeze({ zone: label.zone, x: point.x, y: point.y });
  }));
  return Object.freeze({ borderPolygon, zoneBoundaries, zoneLabels });
}
